import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Megaphone, 
  Palette, 
  Video, 
  Globe, 
  Share2, 
  Mail,
  ArrowRight,
  Check
} from 'lucide-react';
import { PageSEO } from '../components/SEO';

const services = [
  {
    icon: Megaphone,
    title: 'ADVERTISING CAMPAIGNS',
    description: 'Strategic paid media campaigns across social platforms, Google, and programmatic networks to drive awareness and viewership.',
    features: ['Social Media Ads', 'Google & YouTube Ads', 'Programmatic Display', 'Influencer Partnerships'],
  },
  {
    icon: Palette,
    title: 'POSTER DESIGN',
    description: 'Eye-catching key art and poster designs that capture the essence of your film and stand out in crowded marketplaces.',
    features: ['Key Art Development', 'Platform-Specific Formats', 'Alternative Posters', 'Character Posters'],
  },
  {
    icon: Video,
    title: 'TRAILER PRODUCTION',
    description: 'Professional trailer editing that hooks viewers and drives them to watch your film. From teasers to full theatrical trailers.',
    features: ['Teaser Trailers', 'Official Trailers', 'TV Spots', 'Social Media Cuts'],
  },
  {
    icon: Globe,
    title: 'WEBSITE DEVELOPMENT',
    description: 'Custom film websites that serve as your project\'s digital home—complete with trailers, press kits, and screening info.',
    features: ['Custom Design', 'Press Kit Integration', 'Screening Calendars', 'Newsletter Signup'],
  },
  {
    icon: Share2,
    title: 'SOCIAL MEDIA',
    description: 'Full social media management to build and engage your audience across Instagram, Twitter, Facebook, TikTok, and YouTube.',
    features: ['Content Strategy', 'Community Management', 'Influencer Outreach', 'Analytics & Reporting'],
  },
  {
    icon: Mail,
    title: 'EMAIL MARKETING',
    description: 'Direct-to-fan communication strategies that keep your audience engaged from pre-release through distribution.',
    features: ['Campaign Strategy', 'Template Design', 'List Management', 'Performance Analytics'],
  },
];

export default function Marketing() {
  return (
    <div data-testid="marketing-page" className="min-h-screen pt-20">
      <PageSEO page="marketing" />
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1536240478700-b869070f9279?w=1920&q=80"
            alt="Film marketing campaign - professional film poster and trailer production UK"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/80 to-neutral-950/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-6">
              MARKETING AGENCY
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              MAKE YOUR FILM<br />
              <span className="text-orange-500">UNMISSABLE</span>
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-xl">
              Our in-house marketing agency creates compelling campaigns that connect your film with the audiences who will love it most. Every project is bespoke.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              OUR SERVICES
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
              FULL-SERVICE MARKETING
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 border border-white/10 bg-neutral-900/50 hover:bg-neutral-900 transition-colors group"
              >
                <service.icon className="w-10 h-10 text-orange-500 mb-6" />
                <h3 className="font-display text-xl tracking-wider text-white mb-4 uppercase">{service.title}</h3>
                <p className="text-neutral-400 leading-relaxed mb-6 text-sm">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-neutral-500 text-sm">
                      <Check className="w-4 h-4 text-orange-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Marketing Matters */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                WHY IT MATTERS
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                A GREAT FILM DESERVES A GREAT CAMPAIGN
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-6">
                You've spent years making your film. Now it needs to be seen. In today's crowded streaming landscape, even the best content can get lost without strategic marketing.
              </p>
              <p className="text-neutral-400 text-lg leading-relaxed mb-6">
                Our team understands the film industry. We know how to position independent films to compete with studio releases, how to target niche audiences who will become passionate advocates, and how to stretch marketing budgets for maximum impact.
              </p>
              <p className="text-neutral-400 text-lg leading-relaxed">
                From eye-catching posters to scroll-stopping trailers to campaigns that build real communities around your work—we've got you covered.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_filmmaker-profits/artifacts/4rcgvilk_patriarch%20main%202nd%20new%20poster.jpg"
                alt="Patriarch film poster - professional film poster design by LBR Distribution"
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bespoke Approach */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              TAILORED TO YOU
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
              EVERY PROJECT IS BESPOKE
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-8">
              We don't believe in one-size-fits-all packages. Every film is unique, and your marketing strategy should be too. We work with you to understand your goals, your audience, and your budget—then craft a custom plan that maximizes your film's potential.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase text-white mb-6">
              READY TO AMPLIFY YOUR FILM?
            </h2>
            <p className="text-neutral-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Let's discuss how our marketing team can help your film reach its full potential.
            </p>
            <Link
              to="/contact"
              data-testid="marketing-cta-btn"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
            >
              DISCUSS YOUR PROJECT
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
